import { useEffect, useState } from 'react';

/**
 * Returns true when the floating action button should hide itself.
 * Suppression happens when high-priority overlays are open (modals, search, palette)
 * or when a bottom anchor such as the system footer is visible in the viewport.
 */
export function useFabSuppression(): boolean {
  const [overlaySuppressed, setOverlaySuppressed] = useState(false);
  const [anchorVisible, setAnchorVisible] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const hasDialog = Boolean(
        document.querySelector('[data-radix-portal] [data-state="open"]')
      );
      const paletteOpen = document.body.getAttribute('data-command-palette-open') === 'true';
      const adminFooterVisible = document.body.getAttribute('data-admin-footer-visible') === 'true';
      const next = hasDialog || paletteOpen || adminFooterVisible;
      setOverlaySuppressed(prev => (prev === next ? prev : next));
    };

    evaluate();

    const observer = new MutationObserver(() => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(evaluate);
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'data-state',
        'data-command-palette-open',
        'data-admin-footer-visible',
        'style',
        'class'
      ]
    });

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    const elements = Array.from(
      document.querySelectorAll('[data-fab-bottom-anchor]')
    ) as HTMLElement[];

    if (elements.length === 0) {
      setAnchorVisible(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some(entry => entry.isIntersecting);
        setAnchorVisible(prev => (prev === visible ? prev : visible));
      },
      { threshold: 0.1 }
    );

    elements.forEach(element => observer.observe(element));

    return () => {
      observer.disconnect();
    };
  }, []);

  return overlaySuppressed || anchorVisible;
}
